/**
 * What the settings editors refuse before the server has to.
 *
 * The rules live here rather than in the editors so the Save button can ask
 * the same questions the red text under each field asks. The editors keep
 * rendering their own messages; this module only decides.
 */

const FIELD = /\{([^{}]*)\}/g;

export const SLOT_NAMES = ["morning", "afternoon", "evening"];
export const WEEKDAYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

const minutes = (value) => {
  const [h, m] = String(value || "").split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

/**
 * An empty box is "not yet a number", never zero: a clinic backspacing to
 * retype 30 must not land on a form that reads "no cooldown" and, on Save,
 * makes every patient callable again tomorrow. A deliberate 0 is fine, since
 * that is how the cooldown is turned off.
 */
export function cooldownProblem(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Enter a number of days, or 0 for no cooldown.";
  }
  if (!Number.isInteger(value)) return "Use a whole number of days.";
  if (value < 0) return "Days cannot be negative.";
  return null;
}

/**
 * The follow-up schedule: 0 to 4 gaps, each a whole number of days between
 * the previous call and the next one.
 */
export function attemptGapsProblem(gaps) {
  const rows = Array.isArray(gaps) ? gaps : [];
  if (rows.length > 4) return "At most 4 follow-up attempts.";
  for (const gap of rows) {
    if (typeof gap !== "number" || !Number.isInteger(gap)) {
      return "Enter a whole number of days for each follow-up.";
    }
    if (gap < 1 || gap > 14) {
      return "Each follow-up must be between 1 and 14 days after the previous call.";
    }
  }
  return null;
}

/**
 * One message (or null) per calling slot, keyed by slot name. Both halves of
 * an overlapping pair are flagged, so it is obvious which two.
 */
export function callingSlotProblems(slots) {
  const out = {};
  const spans = {};
  for (const name of SLOT_NAMES) {
    const [s, e] = slots?.[name] || ["", ""];
    const start = minutes(s);
    const end = minutes(e);
    if (start === null || end === null) {
      out[name] = "Enter a start and an end time.";
      continue;
    }
    if (end <= start) {
      out[name] = "This slot must end after it starts.";
      continue;
    }
    spans[name] = [start, end];
    out[name] = null;
  }
  for (const a of SLOT_NAMES) {
    for (const b of SLOT_NAMES) {
      if (a !== b && spans[a] && spans[b] && spans[a][0] < spans[b][1] && spans[b][0] < spans[a][1]) {
        out[a] = "These slots overlap.";
      }
    }
  }
  return out;
}

/** At least one calling day must be picked, or nothing ever gets called. */
export function callingDaysProblem(days) {
  return Array.isArray(days) && days.length > 0 ? null : "Pick at least one day.";
}

/** Placeholders in the template that the server does not offer. */
export function unknownPlaceholders(template, placeholders) {
  const known = placeholders || [];
  const used = [...String(template || "").matchAll(FIELD)].map((m) => m[1]);
  return [...new Set(used.filter((name) => !known.includes(name)))];
}

export function smsProblem({ enabled, template, placeholders }) {
  if (!enabled) return null;
  if (!String(template ?? "").trim()) {
    return "Write the message a patient gets, or turn the confirmation off.";
  }
  if (unknownPlaceholders(template, placeholders).length > 0) {
    return "Remove the placeholders the clinic cannot use.";
  }
  return null;
}

// The same shape the sign-in form accepts: something@something.something.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isEmail = (value) => EMAIL.test(String(value || "").trim());

/**
 * The recipients line as the clinic types it: addresses split on commas,
 * semicolons or whitespace, trimmed, in order, each once.
 */
export function parseRecipients(text) {
  const seen = new Set();
  return String(text || "")
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter((part) => part && !seen.has(part) && seen.add(part));
}

/**
 * A report that is on has to go somewhere at some time. One that is off is
 * left alone, whatever the fields hold, so switching it off is always a way
 * out of a half-finished edit.
 */
export function dailyReportProblem({ enabled, recipients, time }) {
  if (!enabled) return null;
  const rows = Array.isArray(recipients) ? recipients : [];
  if (rows.length === 0) return "Add at least one email address for the report.";
  const bad = rows.filter((address) => !isEmail(address));
  if (bad.length > 0) return `Not an email address: ${bad.join(", ")}.`;
  if (!HHMM.test(String(time || ""))) return "Enter the send time as HH:MM, e.g. 16:30.";
  return null;
}

/**
 * The pause after a transfer: nothing (null) for no pause, or a whole number
 * of minutes up to an hour. The server refuses anything else.
 */
export function transferHoldProblem(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return "Use a whole number of minutes between 0 and 60, or leave it empty.";
  }
  if (value < 0 || value > 60) return "The pause must be between 0 and 60 minutes.";
  return null;
}

/** Everything that would make Save a request the server is going to refuse. */
export function settingsProblems(draft) {
  if (!draft) return [];
  const problems = [];
  const cooldown = cooldownProblem(draft.cooldownDays);
  if (cooldown) problems.push(cooldown);
  if (attemptGapsProblem(draft.attemptGapsDays)) {
    problems.push("Fix the follow-up schedule.");
  }
  if (Object.values(callingSlotProblems(draft.callingSlots)).some(Boolean)) {
    problems.push("Fix the calling slots.");
  }
  const days = callingDaysProblem(draft.callingDays);
  if (days) problems.push(days);
  const sms = smsProblem({
    enabled: draft.smsEnabled,
    template: draft.smsTemplate,
    placeholders: draft.placeholders,
  });
  if (sms) problems.push(sms);
  const report = dailyReportProblem({
    enabled: draft.dailyReportEnabled,
    recipients: draft.dailyReportRecipients,
    time: draft.dailyReportTime,
  });
  if (report) problems.push(report);
  const hold = transferHoldProblem(draft.transferHoldMinutes);
  if (hold) problems.push(hold);
  return problems;
}
